import { lazy, Suspense, useState, useEffect, useRef, useCallback } from 'react';
import PageSpinner from './PageSpinner';
import { useNavigate } from 'react-router-dom';
import { Search, X, User, MapPin, Plus, Users, LogIn, Check } from 'lucide-react';
import { API_URL } from '../config';
import { authenticatedFetch } from '../utils/apiUtils';
import { debounce } from 'lodash';
const AddHikeForm = lazy(() => import('./AddHikeForm'));
import { useAuth } from '../contexts/AuthContext';

interface SearchResult {
  _id: string;
  name?: string;
  country?: string;
  difficulty?: string;
  length?: number;
  elevation?: number;
  rating?: number;
  username?: string;
  firstName?: string;
  lastName?: string;
  totalHikes?: number;
  totalLength?: number;
  totalElevation?: number;
  type: 'hike' | 'user';
}

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedHike, setSelectedHike] = useState<SearchResult | null>(null);
  const [showAddHikeForm, setShowAddHikeForm] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [userHikes, setUserHikes] = useState<SearchResult[]>([]);
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch user's hikes when authenticated
  const fetchUserHikes = useCallback(async () => {
    if (!isAuthenticated || !user?._id) return;
    
    try {
      const [myHikesResponse, wishlistResponse] = await Promise.all([
        authenticatedFetch(`${API_URL}/hikes/my-hikes`),
        authenticatedFetch(`${API_URL}/hikes/my-wishlist`)
      ]);

      let allUserHikes: SearchResult[] = [];

      if (myHikesResponse.ok) {
        const myHikesData = await myHikesResponse.json();
        allUserHikes = [...allUserHikes, ...myHikesData.map((hike: any) => ({
          ...hike,
          type: 'hike' as const
        }))];
      }

      if (wishlistResponse.ok) {
        const wishlistData = await wishlistResponse.json();
        allUserHikes = [...allUserHikes, ...wishlistData.map((hike: any) => ({
          ...hike,
          type: 'hike' as const
        }))];
      }

      setUserHikes(allUserHikes);
    } catch (error) {
      console.error('Error fetching user hikes:', error);
    }
  }, [isAuthenticated, user?._id]);

  // Check if user has a specific hike
  const hasHike = useCallback((hikeName: string, hikeCountry: string) => {
    return userHikes.some(hike => 
      hike.name === hikeName && hike.country === hikeCountry
    );
  }, [userHikes]);

  const debouncedSearch = useCallback(
    debounce(async (searchQuery: string) => {
      if (!searchQuery.trim() || searchQuery.trim().length < 2) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Search both hikes and users - no authentication required
        const [hikesResponse, usersResponse] = await Promise.all([
          fetch(`${API_URL}/hikes/search?query=${encodeURIComponent(searchQuery.trim())}`),
          fetch(`${API_URL}/users/search?query=${encodeURIComponent(searchQuery.trim())}`)
        ]);

        let hikes: SearchResult[] = [];
        let users: SearchResult[] = [];

        if (hikesResponse.ok) {
          const hikesData = await hikesResponse.json();
          hikes = hikesData.map((hike: any) => ({
            ...hike,
            type: 'hike' as const
          }));
        }

        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          users = usersData.map((user: any) => ({
            ...user,
            type: 'user' as const
          }));
        }

        // Deduplicate hikes by name and country, keeping the first occurrence
        const uniqueHikes = hikes.reduce((acc: SearchResult[], current) => {
          const key = `${current.name}-${current.country}`;
          const exists = acc.some(hike => `${hike.name}-${hike.country}` === key);
          if (!exists) {
            acc.push(current);
          }
          return acc;
        }, [] as SearchResult[]);

        // Combine results, prioritizing hikes first
        const combinedResults = [...uniqueHikes, ...users];
        setResults(combinedResults);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setShowResults(true);
    
    if (value.trim().length >= 2) {
      debouncedSearch(value);
    } else {
      setResults([]);
    }
  };

  const handleUserClick = (user: SearchResult) => {
    navigate(`/user/${user.username}`);
    onClose();
  };

  const handleHikeClick = (hike: SearchResult) => {
    // Navigate to hike profile page
    const encodedHikeName = encodeURIComponent(hike.name);
    const encodedCountry = encodeURIComponent(hike.country);
    navigate(`/hike/${encodedHikeName}/${encodedCountry}`);
    onClose();
  };

  const handleAddHike = (hike: SearchResult) => {
    if (!isAuthenticated) {
      setSelectedHike(hike);
      setShowLoginPrompt(true);
      setShowResults(false);
      return;
    }
    
    setSelectedHike(hike);
    setShowAddHikeForm(true);
    setShowResults(false);
  };

  const handleHikeFormClose = () => {
    setShowAddHikeForm(false);
    setSelectedHike(null);
  };

  const handleLoginPromptClose = () => {
    setShowLoginPrompt(false);
    setSelectedHike(null);
  };

  const handleLogin = () => {
    onClose();
    navigate('/login');
  };

  const handleHikeFormSubmit = (formData: any) => {
    // Handle successful hike addition
    setShowAddHikeForm(false);
    setSelectedHike(null);
    onClose();
    // Refresh user hikes after adding
    fetchUserHikes();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Fetch user hikes when modal opens and user is authenticated
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchUserHikes();
    }
  }, [isOpen, isAuthenticated, fetchUserHikes]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-b-lg w-full max-w-2xl mx-auto shadow-lg mt-0" ref={searchRef}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Search</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleQueryChange}
                placeholder="Search for hikes or users..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Search for hikes and users. Login to add hikes to your list.
            </p>
          </div>

          {/* Results */}
          {showResults && (
            <div className="max-h-[calc(100vh-200px)] overflow-y-auto border-t dark:border-gray-700">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  Searching...
                </div>
              ) : results.length > 0 ? (
                <div className="divide-y dark:divide-gray-700">
                  {results.map((result, index) => (
                    <div key={`${result.type}-${result._id}-${index}`}>
                      {result.type === 'user' ? (
                        <div
                          onClick={() => handleUserClick(result)}
                          className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                              <Users className="w-5 h-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {result.firstName && result.lastName 
                                  ? `${result.firstName} ${result.lastName}`
                                  : result.username
                                }
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                @{result.username} • {result.totalHikes || 0} hikes
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                              <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div className="flex-1">
                              <div 
                                onClick={() => handleHikeClick(result)}
                                className="font-medium text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer transition-colors"
                              >
                                {result.name}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {result.country} • {result.difficulty} • {result.length}km • {result.elevation}m
                              </div>
                            </div>
                            {isAuthenticated ? (
                              hasHike(result.name!, result.country!) ? (
                                <div className="p-2 text-green-600 dark:text-green-400" title="Already on your list">
                                  <Check className="w-5 h-5" />
                                </div>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddHike(result);
                                  }}
                                  className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                                  title="Add to my list"
                                >
                                  <Plus className="w-5 h-5" />
                                </button>
                              )
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAddHike(result);
                                }}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                                title="Login to add to your list"
                              >
                                <LogIn className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : query.trim().length >= 2 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No results found
                </div>
              ) : null}
            </div>
          )}

          {/* Hike Details Modal */}
          {selectedHike && selectedHike.type === 'hike' && !showAddHikeForm && (
            <div className="p-4 border-t dark:border-gray-700">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedHike.name}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400">{selectedHike.country}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Difficulty</span>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedHike.difficulty}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Rating</span>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedHike.rating}/5</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Length</span>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedHike.length} km</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Elevation</span>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedHike.elevation} m</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  {isAuthenticated && hasHike(selectedHike.name!, selectedHike.country!) ? (
                    <div className="flex-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 py-2 px-4 rounded-lg flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" />
                      Already on your list
                    </div>
                  ) : (
                    <button
                      onClick={() => handleAddHike(selectedHike)}
                      className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                      {isAuthenticated ? (
                        <>
                          <Plus className="w-4 h-4" />
                          Add to My List
                        </>
                      ) : (
                        <>
                          <LogIn className="w-4 h-4" />
                          Login to Add
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setSelectedHike(null);
                      setShowResults(true);
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                  >
                    Back
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Hike Form Modal */}
      {showAddHikeForm && selectedHike && (
        <Suspense fallback={<PageSpinner label="Loading form..." />}>
          <AddHikeForm
            isOpen={showAddHikeForm}
            onClose={handleHikeFormClose}
            onSubmit={handleHikeFormSubmit}
            preFilledHike={selectedHike}
          />
        </Suspense>
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && selectedHike && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Login Required
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                You need to be logged in to add hikes to your list.
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Hike:</span> {selectedHike.name}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedHike.country}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleLoginPromptClose}
                  className="flex-1 px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogin}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
